import {
  StorageOptions,
  StorageStatus,
  StorageError,
  StorageErrorCode,
  type StorageResult,
  type DocumentWriteOptions,
  type RecoveryWriteReport,
  type RecoverySeedReport,
} from "./types";
import { BaseStorage } from "./base-storage";
import { validationSystem } from "./validation";

/** WR-685 D9: an existence probe that has not settled by then answers "unknown". */
const HAS_KEY_TIMEOUT_MS = 2_000;
import {
  decideDocumentWrite,
  projectMetadataKey,
  type WriteDecision,
} from "../documentWriteGuard";
import {
  RECOVERY_RING_CAP,
  armsRecovery,
  isRecoveryRingKey,
  isRecoverySlotKey,
  legacyRecoverySlotKey,
  lsLegacyMoveIsProven,
  planRecoveryAdd,
  planRecoverySeed,
  recoveryRingBounds,
  recoveryRingKey,
  recoveryRingSeq,
} from "../recoveryJournal";
import { logger as _logger } from "../../utils/logger";

/** The `project_` key range of `data_store` (`` ` `` is the code unit after `_`). */
const PROJECT_KEY_LOWER = "project_";
const PROJECT_KEY_UPPER = "project`";

/** The project id of a LEGACY recovery slot key, else null (shape derived from D1's constructor). */
function legacyKeyProjectId(key: string): string | null {
  if (!isRecoverySlotKey(key) || recoveryRingSeq(key) !== null) return null;
  const [head, tail] = legacyRecoverySlotKey("\u0000").split("\u0000");
  if (!key.startsWith(head) || !key.endsWith(tail)) return null;
  const id = key.slice(head.length, key.length - tail.length);
  return id && legacyRecoverySlotKey(id) === key ? id : null;
}

/** The newest (greatest-sequence) ring key of `projectId` among `keys`, else null. */
function newestRingKey(projectId: string, keys: readonly unknown[]): string | null {
  let best: { seq: number; key: string } | null = null;
  for (const k of keys) {
    if (!isRecoveryRingKey(k)) continue;
    const seq = recoveryRingSeq(k);
    if (seq === null || k !== recoveryRingKey(projectId, seq)) continue;
    if (!best || seq > best.seq) best = { seq, key: k };
  }
  return best ? best.key : null;
}

/**
 * THE ONE localStorage READABILITY authority for the recovery journal's LS legacy-copy reads
 * (snapshots-p2a-s11 F1, CTO 66-20). A `SecurityError` on LS access means localStorage is
 * UNREADABLE (site storage disabled) ⇒ there is no LS legacy copy ⇒ `unreadable` is returned.
 * Any OTHER throw is re-thrown, so inside a journal or seed transaction it still ABORTS (R-THROW).
 *
 * ⛔ Not the writability authority: `LocalStorageManager.isStorageAvailable()`
 *    (`local-storage.ts:179`) is a write probe and answers WRITABILITY — a full localStorage is
 *    unwritable yet readable. Do not merge the two.
 */
function lsLegacyRead<T>(read: () => T, unreadable: T): T {
  try {
    return read();
  } catch (error) {
    if ((error as { name?: unknown } | null)?.name === "SecurityError") {
      console.warn("[RECOVERY-JOURNAL] localStorage unreadable (SecurityError) — no LS legacy copy");
      return unreadable;
    }
    throw error;
  }
}

/** Projects whose localStorage tier holds a LEGACY recovery slot key (sync scan). */
function scanLsLegacyRecoveryProjects(): string[] {
  return lsLegacyRead(() => {
    const ids: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      const id = k === null ? null : legacyKeyProjectId(k);
      if (id !== null) ids.push(id);
    }
    return ids;
  }, [] as string[]);
}

function isQuotaError(e: unknown): boolean {
  return !!e && typeof e === "object" && (e as { name?: unknown }).name === "QuotaExceededError";
}

/**
 * IndexedDB implementation of StorageLayer
 */
export class IndexedDBManager extends BaseStorage {
  private readonly DB_NAME = "stagecraft_storage";
  private readonly STORE_NAME = "data_store";
  private readonly DB_VERSION = 1;
  private db: IDBDatabase | null = null;

  constructor() {
    super("indexedDB");
  }

  /**
   * Initialize database connection
   */
  private async initDB(): Promise<IDBDatabase> {
    if (this.db) {
      return this.db;
    }

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.DB_NAME, this.DB_VERSION);

      request.onerror = () => {
        reject(
          new StorageError(
            "Failed to open database",
            StorageErrorCode.NOT_AVAILABLE,
            { error: request.error },
          ),
        );
      };

      request.onsuccess = () => {
        const db = request.result;
        // SG-W3: if ANOTHER tab upgrades the DB, close + clear this cached
        // connection so it neither blocks the upgrade nor serves a stale handle
        // (the next getStore re-opens).
        db.onversionchange = () => {
          db.close();
          this.db = null;
        };
        this.db = db;
        resolve(db);
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Create object store if it doesn't exist
        if (!db.objectStoreNames.contains(this.STORE_NAME)) {
          db.createObjectStore(this.STORE_NAME);
        }
      };
    });
  }

  /**
   * Get database connection
   */
  private async getDB(): Promise<IDBDatabase> {
    try {
      return await this.initDB();
    } catch (error) {
      throw new StorageError(
        "Database not available",
        StorageErrorCode.NOT_AVAILABLE,
        { error },
      );
    }
  }

  /**
   * Create transaction and get object store
   */
  private async getStore(mode: IDBTransactionMode): Promise<IDBObjectStore> {
    const db = await this.getDB();
    const transaction = db.transaction(this.STORE_NAME, mode);
    return transaction.objectStore(this.STORE_NAME);
  }

  /**
   * SG-W2: reject a pending operation if its TRANSACTION aborts (e.g. quota
   * exceeded, or a versionchange forcing close). A transaction-level abort may
   * not surface via the request's `onerror`, so without this the awaiting caller
   * would hang forever. Whichever of request/transaction settles the promise
   * first wins; a later reject on an already-settled promise is a no-op.
   */
  private rejectOnTxAbort(
    store: IDBObjectStore,
    reject: (reason: unknown) => void,
  ): void {
    // A real IDBObjectStore always exposes `.transaction`; guard anyway so a
    // hand-rolled test mock without one is a no-op rather than a throw.
    const tx = store.transaction;
    if (!tx) return;
    tx.onabort = () =>
      reject(
        new StorageError(
          "IndexedDB transaction aborted",
          StorageErrorCode.OPERATION_FAILED,
          { error: tx.error },
        ),
      );
  }

  /**
   * Get storage status
   */
  async getStatus(): Promise<StorageStatus> {
    try {
      const db = await this.getDB();
      const transaction = db.transaction(this.STORE_NAME, "readonly");
      const store = transaction.objectStore(this.STORE_NAME);

      return new Promise((resolve) => {
        // Get count of items
        const countRequest = store.count();

        countRequest.onsuccess = () => {
          resolve({
            available: true,
            usedSpace: 0, // IndexedDB doesn't provide space usage
            totalSpace: Infinity, // IndexedDB uses available disk space
            operations: {
              pending: 0, // IndexedDB doesn't expose pending operations
              completed: countRequest.result,
              failed: 0,
            },
            health: {
              errorRate: 0,
              avgOperationTime: 0,
            },
          });
        };

        countRequest.onerror = () => {
          resolve({
            available: false,
            usedSpace: 0,
            totalSpace: 0,
            errorMessage: "Failed to get storage status",
          });
        };
      });
    } catch (error) {
      return {
        available: false,
        usedSpace: 0,
        totalSpace: 0,
        errorMessage: "Database not available",
      };
    }
  }

  /**
   * Implement storage-specific save operation
   */
  protected async performSave<T>(
    key: string,
    data: T,
    _options?: StorageOptions,
  ): Promise<void> {
    const store = await this.getStore("readwrite");

    return new Promise((resolve, reject) => {
      const request = store.put(data, key);
      this.rejectOnTxAbort(store, reject);

      request.onsuccess = () => resolve();

      request.onerror = () => {
        reject(
          new StorageError(
            "Failed to save data",
            StorageErrorCode.OPERATION_FAILED,
            { error: request.error },
          ),
        );
      };
    });
  }

  /**
   * Implement storage-specific load operation
   */
  protected async performLoad<T>(
    key: string,
    _options?: StorageOptions,
  ): Promise<T> {
    const store = await this.getStore("readonly");

    return new Promise((resolve, reject) => {
      const request = store.get(key);
      this.rejectOnTxAbort(store, reject);

      request.onsuccess = () => {
        if (request.result === undefined) {
          reject(
            new StorageError("Data not found", StorageErrorCode.NOT_AVAILABLE, {
              notFound: true,
            }),
          );
        } else {
          resolve(request.result);
        }
      };

      request.onerror = () => {
        reject(
          new StorageError(
            "Failed to load data",
            StorageErrorCode.OPERATION_FAILED,
            { error: request.error },
          ),
        );
      };
    });
  }

  /**
   * Implement storage-specific delete operation. A recovery slot key is REFUSED
   * (s10 D2d): backup copies leave `data_store` only through `deleteRecoveryKeys`.
   */
  protected async performDelete(
    key: string,
    _options?: StorageOptions,
  ): Promise<void> {
    if (isRecoverySlotKey(key)) {
      console.error(
        `[RECOVERY-JOURNAL] refused: delete of recovery key ${key} outside deleteRecoveryKeys`,
      );
      throw new StorageError(
        "Recovery key is protected",
        StorageErrorCode.RECOVERY_KEY_PROTECTED,
        { key },
      );
    }
    const store = await this.getStore("readwrite");

    return new Promise((resolve, reject) => {
      const request = store.delete(key);
      this.rejectOnTxAbort(store, reject);

      request.onsuccess = () => resolve();

      request.onerror = () => {
        reject(
          new StorageError(
            "Failed to delete data",
            StorageErrorCode.OPERATION_FAILED,
            { error: request.error },
          ),
        );
      };
    });
  }

  /**
   * Implement storage-specific clear operation
   */
  protected async performClear(_options?: StorageOptions): Promise<void> {
    const store = await this.getStore("readwrite");

    return new Promise((resolve, reject) => {
      const request = store.clear();
      this.rejectOnTxAbort(store, reject);

      request.onsuccess = () => resolve();

      request.onerror = () => {
        reject(
          new StorageError(
            "Failed to clear storage",
            StorageErrorCode.OPERATION_FAILED,
            { error: request.error },
          ),
        );
      };
    });
  }

  /**
   * GUARDED document write (WR-685 hotfix, D2): ONE read-write transaction reads the
   * project's metadata record (liveness) and the stored value at `key` (currency),
   * asks `decideDocumentWrite` (the ONE authority — this method is rule-agnostic), and
   * then either puts or puts NOTHING. A refusal commits an empty transaction — it is never
   * an abort, so it is never mistaken for a storage failure and retried (R4c2-4). Settles
   * on the transaction's `complete` / `abort` / `error` (SG-W2), so the caller never hangs.
   *
   * THE RECOVERY JOURNAL (snapshots-p2a-s10 D2a; P-RING, LD-6): an allowed put that
   * `armsRecovery` arms (BY KEY) keeps the stored record as a ring slot IN THIS SAME
   * TRANSACTION, chained as IDB requests only (no await, no foreign promise). Any journal
   * failure ABORTS the transaction, so the destructive put never lands without its
   * pre-image. A quota failure is a storage failure (retried, `STORAGE_FAILED`); any other
   * journal failure is `SAFETY_COPY_FAILED` (never retried). No handler ever calls
   * `preventDefault` — a request error always aborts.
   *
   * The legacy fallback for liveness (a project whose metadata the boot migration left
   * in localStorage) is read here, synchronously inside the request callback, only
   * when IndexedDB holds no metadata record — the document loader's tier order
   * (`coordinator.loadDocument`).
   */
  async guardedSave<T>(
    key: string,
    data: T,
    write: DocumentWriteOptions = {},
  ): Promise<StorageResult<void>> {
    const keyValidation = validationSystem.validateKey(key);
    const dataValidation = validationSystem.validateData(data);
    if (!keyValidation.isValid || !dataValidation.isValid) {
      return {
        success: false,
        error: new StorageError(
          "Invalid document write",
          StorageErrorCode.VALIDATION_FAILED,
          { errors: [...keyValidation.errors, ...dataValidation.errors] },
        ),
      };
    }

    let db: IDBDatabase;
    try {
      db = await this.getDB();
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof StorageError
            ? error
            : new StorageError(
                "Database not available",
                StorageErrorCode.NOT_AVAILABLE,
                { error },
              ),
      };
    }

    return new Promise<StorageResult<void>>((resolve) => {
      let settled = false;
      const settle = (r: StorageResult<void>) => {
        if (settled) return;
        settled = true;
        resolve(r);
      };
      const fail = (message: string, error: unknown) =>
        settle({
          success: false,
          error: new StorageError(message, StorageErrorCode.OPERATION_FAILED, {
            error,
          }),
        });

      let decision: WriteDecision = {
        kind: "failed",
        error: new Error("undecided"),
      };
      /** what the journal did (set only when the write was armed) */
      let recovery: RecoveryWriteReport | undefined;
      /** a journal failure (throw, request error, armed-without-recovery) */
      let journalFailure: { error: unknown } | null = null;
      /** a QuotaExceededError seen on any request or on the transaction */
      let quotaSeen = false;
      /** Settle a failed transaction: quota ⇒ storage failure; journal ⇒ safety copy. */
      const failClassified = (message: string, error: unknown) => {
        if (quotaSeen || isQuotaError(error) || isQuotaError(tx?.error)) {
          fail(message, error);
        } else if (journalFailure) {
          settle({
            success: false,
            error: new StorageError(
              "Safety copy failed — the write did not land",
              StorageErrorCode.SAFETY_COPY_FAILED,
              { key, error: journalFailure.error },
            ),
          });
        } else {
          fail(message, error);
        }
      };

      let tx: IDBTransaction;
      try {
        tx = db.transaction(this.STORE_NAME, "readwrite");
      } catch (error) {
        fail("IndexedDB transaction could not start", error);
        return;
      }
      tx.oncomplete = () => {
        if (decision.kind === "allow") {
          settle({
            success: true,
            metadata: {
              timestamp: Date.now(),
              operation: "save",
              rev: decision.rev,
              ...(recovery && { recovery }),
            },
          });
        } else if (decision.kind === "noop") {
          // Identical to what is stored: nothing was put, the revision did not move.
          settle({
            success: true,
            metadata: { timestamp: Date.now(), operation: "save", rev: decision.rev },
          });
        } else if (decision.kind === "refuse") {
          settle({
            success: false,
            error: new StorageError(
              "Document write refused",
              StorageErrorCode.WRITE_REFUSED,
              { reason: decision.reason, key },
            ),
            metadata: {
              timestamp: Date.now(),
              operation: "save",
              refusal: decision.reason,
            },
          });
        } else {
          fail("Document write undecided", decision.error);
        }
      };
      tx.onabort = () => failClassified("IndexedDB transaction aborted", tx.error);
      tx.onerror = (event: Event) => {
        const reqError = (event.target as IDBRequest | null)?.error;
        if (isQuotaError(reqError)) quotaSeen = true;
        failClassified("IndexedDB transaction failed", reqError ?? tx.error);
      };

      const store = tx.objectStore(this.STORE_NAME);
      const projectId = write.guard?.projectId;
      const metaKey = projectId ? projectMetadataKey(projectId) : null;
      const metaReq = metaKey ? store.get(metaKey) : null;
      const storedReq = store.get(key);

      /** LD-6: a journal failure is recorded, logged loud, and ABORTS the transaction. */
      const journalFail = (error: unknown) => {
        if (!journalFailure) journalFailure = { error };
        if (isQuotaError(error)) quotaSeen = true;
        console.error(`[RECOVERY-JOURNAL] journal failure on ${key} — aborting the write:`, error);
        try {
          tx.abort();
        } catch {
          // already finishing
        }
      };
      /** Every journal request: onerror → record + abort (never preventDefault). */
      const onJournalError = (req: IDBRequest) => {
        req.onerror = () => journalFail(req.error);
      };
      /** Every chained callback: body in try/catch → abort. */
      const thenJournal = (req: IDBRequest, body: () => void) => {
        onJournalError(req);
        req.onsuccess = () => {
          try {
            body();
          } catch (error) {
            journalFail(error);
          }
        };
      };

      /**
       * The ring add (D2a), chained in THIS transaction: ring keys → legacy slot → LS
       * legacy (sync) → seed → newest → add + ≤ 2 evictions. Planned by D1 only.
       */
      const journal = (pid: string, stored: unknown) => {
        if (!write.recovery) {
          journalFail(
            new Error(`[RECOVERY-JOURNAL] armed write without recovery: ${key}`),
          );
          return;
        }
        const report: RecoveryWriteReport = { seeded: [], evicted: [] };
        recovery = report;
        const candidate = write.recovery.candidate(stored);
        if (candidate === undefined) return;
        const bounds = recoveryRingBounds(pid);
        const legacyKey = legacyRecoverySlotKey(pid);
        const keysReq = store.getAllKeys(IDBKeyRange.bound(bounds.lower, bounds.upper));
        thenJournal(keysReq, () => {
          const ringKeys = keysReq.result as IDBValidKey[];
          const legacyReq = store.get(legacyKey);
          thenJournal(legacyReq, () => {
            const legacyLsRaw = lsLegacyRead(() => localStorage.getItem(legacyKey), null);
            const seeds = planRecoverySeed({
              projectId: pid,
              ringKeys,
              legacyIdb: legacyReq.result,
              legacyLsRaw,
            });
            for (const s of seeds) {
              onJournalError(store.add(s.value, s.key));
              report.seeded.push(s.key);
            }
            const postKeys: unknown[] = [...ringKeys, ...seeds.map((s) => s.key)];
            const plan = (newest: unknown) => {
              const p = planRecoveryAdd({
                projectId: pid,
                ringKeys: postKeys,
                newest,
                candidate,
                now: Date.now(),
                cap: RECOVERY_RING_CAP,
              });
              if (p.add) {
                onJournalError(store.add(p.add.value, p.add.key));
                report.added = p.add.key;
              }
              for (const k of p.evict) {
                onJournalError(store.delete(k));
                report.evicted.push(k);
              }
            };
            const newestKey = newestRingKey(pid, postKeys);
            const seeded = seeds.find((s) => s.key === newestKey);
            if (seeded) {
              plan(seeded.value);
            } else if (newestKey === null) {
              plan(undefined);
            } else {
              const newestReq = store.get(newestKey);
              thenJournal(newestReq, () => plan(newestReq.result));
            }
          });
        });
      };
      storedReq.onsuccess = () => {
        try {
          let metadataExists = !!metaReq && metaReq.result !== undefined;
          if (!metadataExists && metaKey) {
            try {
              metadataExists = localStorage.getItem(metaKey) !== null;
            } catch {
              // localStorage unavailable — no legacy copy to honour.
            }
          }
          const stored = storedReq.result;
          decision = decideDocumentWrite(
            key,
            data,
            write.guard,
            metadataExists,
            stored,
          );
          if (decision.kind === "allow") {
            store.put(decision.value, key);
            if (projectId && armsRecovery(key, data, projectId)) {
              try {
                journal(projectId, stored);
              } catch (error) {
                journalFail(error);
              }
            }
          } else if (decision.kind === "failed") {
            tx.abort();
          }
          // "refuse": no request — the transaction commits empty.
        } catch (error) {
          decision = { kind: "failed", error };
          try {
            tx.abort();
          } catch {
            // already finishing
          }
        }
      };
    });
  }

  /**
   * Does `key` exist in the document store? `null` when the store cannot be read
   * (the caller decides what "unknown" means — never conflated with "absent").
   * WR-685 D9: the sidecar writer's liveness probe.
   */
  async hasKey(key: string): Promise<boolean | null> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      const store = await this.getStore("readonly");
      return await new Promise<boolean | null>((resolve) => {
        // Bounded: a lookup that never settles answers "unknown", never hangs the
        // sidecar write that waits on it.
        timer = setTimeout(() => resolve(null), HAS_KEY_TIMEOUT_MS);
        const request = store.count(key);
        request.onsuccess = () => resolve(request.result > 0);
        request.onerror = () => resolve(null);
      });
    } catch {
      return null;
    } finally {
      clearTimeout(timer);
    }
  }

  /**
   * Enumerate every stored key that begins with `prefix` (registry-INDEPENDENT).
   *
   * Bombproof storage foundation, step 3 (approval-gate §6.3 / RESHAPE-3): the raw
   * two-tier `project_<id>_*` delete-sweep needs to discover the actual on-disk keys
   * for a project WITHOUT consulting any registry (the registry under-reclaims). This
   * reads `data_store`'s keyspace directly via `getAllKeys()` and filters by prefix.
   *
   * Fail-safe: if the database is unavailable, returns [] (the caller still sweeps
   * the localStorage tier). Only ever operates on this manager's own store
   * (`data_store` in `stagecraft_storage`); the dedicated asset DB `stagecraft_assets`
   * is a SEPARATE database and is never reachable from here.
   */
  async getKeysByPrefix(prefix: string): Promise<string[]> {
    try {
      const store = await this.getStore("readonly");
      return await new Promise<string[]>((resolve, reject) => {
        const request = store.getAllKeys();
        this.rejectOnTxAbort(store, reject);
        request.onsuccess = () => {
          const keys = (request.result as IDBValidKey[]).filter(
            (k): k is string => typeof k === "string" && k.startsWith(prefix),
          );
          resolve(keys);
        };
        request.onerror = () =>
          reject(
            new StorageError(
              "Failed to enumerate keys",
              StorageErrorCode.OPERATION_FAILED,
              { error: request.error },
            ),
          );
      });
    } catch {
      // Database unavailable — the localStorage tier sweep still runs.
      return [];
    }
  }

  /** One readonly request on `data_store`, settled by the request or the transaction. */
  private async readOnce<R>(
    make: (store: IDBObjectStore) => IDBRequest<R>,
  ): Promise<R> {
    const store = await this.getStore("readonly");
    return new Promise<R>((resolve, reject) => {
      const req = make(store);
      this.rejectOnTxAbort(store, reject);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }

  /**
   * THE EAGER BOOT SEED + the proven LS tier move (snapshots-p2a-s10 D2b/D2c; CTO 65-81 (a),
   * 65-82, 65-85 FR-P2). Never throws; every failure is logged loud and recorded.
   *
   * SEED (ADD-ONLY): for each project holding a legacy slot (`legacyRecoverySlotKey`) in
   * either tier and NO ring key, ONE readwrite transaction reads the METADATA record first
   * (IndexedDB, else the localStorage fallback — exactly as `guardedSave`) and skips a dead
   * project (P-685 LIVE, FR-R1); re-reads the ring range (a second tab already seeded ⇒
   * nothing); then `planRecoverySeed` → `store.add` each. It never puts or deletes.
   *
   * TIER MOVE: see `moveProvenLsLegacySlots`.
   */
  async seedRecoveryRings(): Promise<RecoverySeedReport> {
    const t0 = performance.now();
    const report: RecoverySeedReport = {
      candidates: 0,
      seeded: [],
      skippedDead: [],
      moved: [],
      failed: [],
      ms: 0,
    };
    const recordFailure = (projectId: string, error: unknown) => {
      const message = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
      report.failed.push({ projectId, error: message });
      console.error(`[RECOVERY-JOURNAL] seed failed for project ${projectId}:`, error);
    };

    let db: IDBDatabase | null = null;
    try {
      db = await this.getDB();
      const keys = await this.readOnce((s) =>
        s.getAllKeys(IDBKeyRange.bound(PROJECT_KEY_LOWER, PROJECT_KEY_UPPER, false, true)),
      );
      const withLegacy = new Set<string>();
      for (const k of keys) {
        const id = typeof k === "string" ? legacyKeyProjectId(k) : null;
        if (id !== null) withLegacy.add(id);
      }
      for (const id of scanLsLegacyRecoveryProjects()) withLegacy.add(id);
      report.candidates = withLegacy.size;

      for (const projectId of withLegacy) {
        if (newestRingKey(projectId, keys) !== null) continue; // already has a ring
        try {
          const outcome = await this.seedOneProject(db, projectId);
          if (outcome === "dead") report.skippedDead.push(projectId);
          else report.seeded.push(...outcome);
        } catch (error) {
          recordFailure(projectId, error);
        }
      }
    } catch (error) {
      recordFailure("*", error);
    }

    if (db) {
      try {
        await this.moveProvenLsLegacySlots(report);
      } catch (error) {
        recordFailure("*", error);
      }
    }

    report.ms = performance.now() - t0;
    console.log(
      `[RECOVERY-JOURNAL] seed pass: candidates=${report.candidates} seeded=${report.seeded.length} ` +
        `skippedDead=${report.skippedDead.length} moved=${report.moved.length} ` +
        `failed=${report.failed.length} ms=${report.ms.toFixed(1)}`,
    );
    return report;
  }

  /** One project's seed transaction (D2b): liveness → ring re-read → legacy → add. */
  private seedOneProject(
    db: IDBDatabase,
    projectId: string,
  ): Promise<string[] | "dead"> {
    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.STORE_NAME, "readwrite");
      const store = tx.objectStore(this.STORE_NAME);
      const written: string[] = [];
      let dead = false;
      let failure: { error: unknown } | null = null;
      const abortWith = (error: unknown) => {
        if (!failure) failure = { error };
        try {
          tx.abort();
        } catch {
          // already finishing
        }
      };
      const then = (req: IDBRequest, body: () => void) => {
        req.onerror = () => abortWith(req.error);
        req.onsuccess = () => {
          try {
            body();
          } catch (error) {
            abortWith(error);
          }
        };
      };
      tx.oncomplete = () => resolve(dead ? "dead" : written);
      tx.onabort = () => reject(failure ? failure.error : tx.error);

      const metaKey = projectMetadataKey(projectId);
      const metaReq = store.get(metaKey);
      then(metaReq, () => {
        let alive = metaReq.result !== undefined;
        if (!alive) {
          try {
            alive = localStorage.getItem(metaKey) !== null;
          } catch {
            // localStorage unavailable — no legacy copy to honour.
          }
        }
        if (!alive) {
          dead = true; // a dead project is never seeded (FR-R1); the transaction commits empty
          return;
        }
        const bounds = recoveryRingBounds(projectId);
        const ringReq = store.getAllKeys(IDBKeyRange.bound(bounds.lower, bounds.upper));
        then(ringReq, () => {
          const legacyKey = legacyRecoverySlotKey(projectId);
          const legacyReq = store.get(legacyKey);
          then(legacyReq, () => {
            const seeds = planRecoverySeed({
              projectId,
              ringKeys: ringReq.result as IDBValidKey[],
              legacyIdb: legacyReq.result,
              legacyLsRaw: lsLegacyRead(() => localStorage.getItem(legacyKey), null),
            });
            for (const s of seeds) {
              const addReq = store.add(s.value, s.key);
              addReq.onerror = () => abortWith(addReq.error);
              written.push(s.key);
            }
          });
        });
      });
    });
  }

  /**
   * D2c — THE ONE EXCEPTION TO ADD-ONLY (CTO 65-82): the proven tier move of a project's
   * localStorage legacy recovery slot. For EVERY project whose LS holds a legacy key
   * (including rings from an earlier session — FR-P2 = yes, CTO 65-85): read the ring back
   * from IndexedDB; then, in ONE synchronous turn, re-read the LS value and remove it only
   * if `lsLegacyMoveIsProven` (compare-and-delete — never delete-by-snapshot). Never when
   * the read-back failed; never any other key; the IndexedDB legacy key is never touched.
   */
  private async moveProvenLsLegacySlots(report: RecoverySeedReport): Promise<void> {
    for (const projectId of scanLsLegacyRecoveryProjects()) {
      const legacyKey = legacyRecoverySlotKey(projectId);
      let readBack: unknown[];
      try {
        const bounds = recoveryRingBounds(projectId);
        readBack = await this.readOnce((s) =>
          s.getAll(IDBKeyRange.bound(bounds.lower, bounds.upper)),
        );
      } catch (error) {
        console.error(
          `[RECOVERY-JOURNAL] tier move: read-back failed for ${projectId} — LS copy kept:`,
          error,
        );
        continue;
      }
      // ONE synchronous turn: re-read, prove, delete.
      const lsNow = localStorage.getItem(legacyKey);
      if (lsLegacyMoveIsProven(lsNow, readBack)) {
        localStorage.removeItem(legacyKey);
        report.moved.push(projectId);
        console.log(`[RECOVERY-JOURNAL] tier move: ${legacyKey} proven in the ring — LS copy removed`);
      } else {
        console.log(`[RECOVERY-JOURNAL] tier move: ${legacyKey} not proven — LS copy kept`);
      }
    }
  }

  /**
   * THE ONE DOOR that deletes recovery slot keys from `data_store` (s10 D2d). Every key must
   * satisfy `isRecoverySlotKey` (any other key is refused into `failed`). One readwrite
   * transaction; `cause` names the caller in the log.
   */
  async deleteRecoveryKeys(
    keys: string[],
    cause: string,
  ): Promise<{ deleted: string[]; failed: string[] }> {
    const accepted = keys.filter((k) => isRecoverySlotKey(k));
    const refused = keys.filter((k) => !isRecoverySlotKey(k));
    if (refused.length > 0) {
      console.error(
        `[RECOVERY-JOURNAL] deleteRecoveryKeys(${cause}) refused non-recovery keys:`,
        refused,
      );
    }
    if (accepted.length === 0) return { deleted: [], failed: refused };
    try {
      const db = await this.getDB();
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(this.STORE_NAME, "readwrite");
        const store = tx.objectStore(this.STORE_NAME);
        for (const k of accepted) store.delete(k);
        tx.oncomplete = () => resolve();
        tx.onabort = () => reject(tx.error);
      });
      console.log(
        `[RECOVERY-JOURNAL] deleteRecoveryKeys(${cause}): deleted ${accepted.length} recovery key(s)`,
      );
      return { deleted: accepted, failed: refused };
    } catch (error) {
      console.error(`[RECOVERY-JOURNAL] deleteRecoveryKeys(${cause}) failed:`, error);
      return { deleted: [], failed: [...refused, ...accepted] };
    }
  }

  /**
   * Close database connection
   */
  async close(): Promise<void> {
    if (this.db) {
      this.db.close();
      this.db = null;
    }
  }

  /**
   * Delete database
   */
  async deleteDatabase(): Promise<void> {
    await this.close();

    return new Promise((resolve, reject) => {
      const request = indexedDB.deleteDatabase(this.DB_NAME);

      request.onsuccess = () => resolve();

      request.onerror = () => {
        reject(
          new StorageError(
            "Failed to delete database",
            StorageErrorCode.OPERATION_FAILED,
            { error: request.error },
          ),
        );
      };
    });
  }
}

// Export singleton instance
export const indexedDBManager = new IndexedDBManager();
